// src/pages/Dashboard/DashboardPage.tsx

import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Activity, BarChart3, AlertTriangle, History, TrendingUp, Zap } from "lucide-react";
import backendApi from "../../../services/backendApi";
import PowerBIEmbed from "./PowerBIEmbed";
import { useTheme } from "../../../components/ThemeContext";

// Each dashboard is its own chunk, downloaded the first time its category is opened
const Live2Dashboard = React.lazy(() => import("./Live2Dashboard"));
const PerformanceAnalytics2Dashboard = React.lazy(() => import("./PerformanceAnalytics2Dashboard"));
const HistorianChart = React.lazy(() => import("./HistorianChart"));
const ForcedOutagesChart = React.lazy(() => import("./ForcedOutagesChart"));

// Native dashboard tab id per category
const NATIVE_TAB: Partial<Record<string, string>> = {
  'live-2': 'live-2-custom',
  'performance-analytics-2': 'performance-analytics-2-custom',
  'historian': 'historical-analysis',
  'forced-outages': 'plant-breakdown-analysis',
};

const DashboardFallback = () => {
  const { isDarkMode } = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '240px' }}>
      <div style={{ width: '32px', height: '32px', border: isDarkMode ? '3px solid #1E2B42' : '3px solid #E5E7EB', borderTopColor: '#0084CF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );
};

// Parse reportId, appId, ctid from a Power BI embed URL
function parsePowerBIUrl(url: string): { reportId: string; appId: string; ctid: string } {
  try {
    const params = new URLSearchParams(url.split('?')[1] || '');
    return {
      reportId: params.get('reportId') || '',
      appId: params.get('appId') || '',
      ctid: params.get('ctid') || '',
    };
  } catch {
    return { reportId: '', appId: '', ctid: '' };
  }
}

type DashboardCategory = 'live' | 'live-2' | 'performance-analytics' | 'performance-analytics-2' | 'forced-outages' | 'historian' | 'virtual-power-plant';

const resolveCategoryFromParam = (param: string | null): DashboardCategory | null => {
  if (!param) return null;
  const p = param.toLowerCase().trim();
  if (p === 'live' || p === 'live-2') return 'live-2';
  if (p === 'performance-analytics' || p === 'performance-analytics-2' || p === 'analytics' || p === 'performance') return 'performance-analytics-2';
  if (p === 'forced-outages' || p === 'outages' || p === 'forced') return 'forced-outages';
  if (p === 'historian' || p === 'historical' || p === 'history') return 'historian';
  if (p === 'virtual-power-plant' || p === 'vpp') return 'virtual-power-plant';
  return null;
};

interface TabInfo {
  id: string;
  name: string;
  url: string;
  icon: React.ReactNode;
  category: DashboardCategory;
}

const DashboardPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [activeCategory, setActiveCategory] = useState<DashboardCategory>(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const urlCat = resolveCategoryFromParam(searchParams.get('tab') || searchParams.get('category'));
      if (urlCat) return urlCat;

      const stored = localStorage.getItem('dashboard_active_category');
      const storedCat = resolveCategoryFromParam(stored);
      if (storedCat) return storedCat;
    } catch (e) {}
    return 'live-2';
  });

  // Keep category in sync with URL query param ?tab=... or ?category=...
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlCat = resolveCategoryFromParam(searchParams.get('tab') || searchParams.get('category'));
    if (urlCat && urlCat !== activeCategory) {
      setActiveCategory(urlCat);
      try {
        localStorage.setItem('dashboard_active_category', urlCat);
      } catch (e) {}
    }
  }, [location.search, activeCategory]);

  const handleSelectCategory = (category: string) => {
    const cat = resolveCategoryFromParam(category) || (category as DashboardCategory);
    setActiveCategory(cat);
    try {
      localStorage.setItem('dashboard_active_category', cat);
    } catch (e) {}
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', cat);
    navigate(`/dashboard/${site}?${searchParams.toString()}`, { replace: true });
  };
  const [activeTab, setActiveTab] = useState<string>(() => {
    try {
      const stored = localStorage.getItem('dashboard_active_tab');
      if (stored) return stored;
    } catch (e) {}
    return '';
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [siteData, setSiteData] = useState<any>(null);
  const [loadingSite, setLoadingSite] = useState<boolean>(true);

  // Tracks which tabs have ever been activated (so component state is preserved across tab switches).
  // Only the opened dashboard mounts; mounting all four up front loaded and rendered every dashboard at once.
  const [activatedTabs, setActivatedTabs] = useState<Set<string>>(() => {
    const first = NATIVE_TAB[activeCategory];
    return new Set<string>(first ? [first] : []);
  });

  useEffect(() => {
    const id = NATIVE_TAB[activeCategory];
    if (!id) return;
    setActivatedTabs(prev => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, [activeCategory]);

  // Keep ?tab= in the URL so refresh, back/forward and the sidebar all show the same dashboard
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (resolveCategoryFromParam(searchParams.get('tab') || searchParams.get('category'))) return;
    searchParams.set('tab', activeCategory);
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  }, [location.pathname, location.search, activeCategory, navigate]);
  // Tracks which tabs have fully loaded (so we skip the loading overlay on revisit)
  const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set<string>());

  // Fetch site data on load / site_code change
  useEffect(() => {
    if (!site) {
      setLoadingSite(false);
      return;
    }
    
    let isMounted = true;
    setLoadingSite(true);
    
    backendApi.getSites(false, site)
      .then((res: any) => {
        if (isMounted) {
          if (res.success && res.sites && res.sites.length > 0) {
            const matchedSite = res.sites.find((s: any) => s.site_code?.toUpperCase() === site.toUpperCase());
            setSiteData(matchedSite || null);
          } else {
            setSiteData(null);
          }
          setLoadingSite(false);
        }
      })
      .catch((err: any) => {
        console.error("Error fetching site details:", err);
        if (isMounted) {
          setSiteData(null);
          setLoadingSite(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [site]);
  // Construct dynamic tabs list based on fetched siteData
  const allTabs: TabInfo[] = [];
  if (siteData) {
    // 1. Live Dashboards
    if (siteData.live_iframe_url) {
      try {
        const parsed = JSON.parse(siteData.live_iframe_url);
        if (Array.isArray(parsed) && parsed.length > 0) {
          parsed.forEach((item: any, index: number) => {
            if (item.url) {
              allTabs.push({
                id: `live-embed-${index}`,
                name: item.name || `${siteData.site_name} Live ${index + 1}:`,
                url: item.url,
                icon: <Activity size={16} />,
                category: "live"
              });
            }
          });
        } else {
          allTabs.push({
            id: "live-embed",
            name: `${siteData.site_name} Live:`,
            url: siteData.live_iframe_url,
            icon: <Activity size={16} />,
            category: "live"
          });
        }
      } catch {
        allTabs.push({
          id: "live-embed",
          name: `${siteData.site_name} Live:`,
          url: siteData.live_iframe_url,
          icon: <Activity size={16} />,
          category: "live"
        });
      }
    }

    // 2. Analytics Dashboards
    if (siteData.analytics_iframe_url) {
      try {
        const parsed = JSON.parse(siteData.analytics_iframe_url);
        if (Array.isArray(parsed) && parsed.length > 0) {
          parsed.forEach((item: any, index: number) => {
            if (item.url) {
              allTabs.push({
                id: `analytics-embed-${index}`,
                name: item.name || `${siteData.site_name} Analytics ${index + 1}:`,
                url: item.url,
                icon: <BarChart3 size={16} />,
                category: "performance-analytics"
              });
            }
          });
        } else {
          allTabs.push({
            id: "analytics-embed",
            name: `${siteData.site_name} Analytics:`,
            url: siteData.analytics_iframe_url,
            icon: <BarChart3 size={16} />,
            category: "performance-analytics"
          });
        }
      } catch {
        allTabs.push({
          id: "analytics-embed",
          name: `${siteData.site_name} Analytics:`,
          url: siteData.analytics_iframe_url,
          icon: <BarChart3 size={16} />,
          category: "performance-analytics"
        });
      }
    }
  }

  // Add the virtual tabs for Live 2, Performance Analytics 2, Historian, and Forced Outages
  allTabs.push({
    id: "live-2-custom",
    name: "Live 2",
    url: "",
    icon: <Activity size={16} />,
    category: "live-2"
  });
  allTabs.push({
    id: "performance-analytics-2-custom",
    name: "Performance Analytics 2",
    url: "",
    icon: <TrendingUp size={16} />,
    category: "performance-analytics-2"
  });
  allTabs.push({
    id: "historical-analysis",
    name: "Historical Analysis",
    url: "",
    icon: <History size={16} />,
    category: "historian"
  });
  allTabs.push({
    id: "plant-breakdown-analysis",
    name: "Plant Breakdown Analysis",
    url: "",
    icon: <AlertTriangle size={16} />,
    category: "forced-outages"
  });

  const categoryTabs = allTabs.filter(t => t.category === activeCategory);
  
  const shouldShowSubTabs =
    activeCategory !== 'historian' &&
    activeCategory !== 'forced-outages' &&
    activeCategory !== 'live-2' &&
    activeCategory !== 'performance-analytics-2' &&
    activeCategory !== 'virtual-power-plant' &&
    categoryTabs.length > 1;

  // Sync category selection with activeTab
  useEffect(() => {
    try {
      localStorage.setItem('dashboard_active_category', activeCategory);
    } catch (e) {}
  }, [activeCategory]);

  useEffect(() => {
    if (activeTab) {
      try {
        localStorage.setItem('dashboard_active_tab', activeTab);
      } catch (e) {}
    }
  }, [activeTab]);

  // Sync category selection with activeTab
  useEffect(() => {
    if (loadingSite) return;

    // Check if there is a stored tab for this category that is valid
    try {
      const storedTab = localStorage.getItem('dashboard_active_tab');
      if (storedTab && categoryTabs.some(t => t.id === storedTab)) {
        setActiveTab(storedTab);
        return;
      }
    } catch (e) {}

    if (categoryTabs.length > 0) {
      const defaultTab = categoryTabs[0].id;
      if (!categoryTabs.some(t => t.id === activeTab)) {
        setActiveTab(defaultTab);
      }
    } else {
      setActiveTab('');
    }
  }, [activeCategory, siteData, loadingSite]);

  // Track activation and loading of tabs
  useEffect(() => {
    if (!activeTab) return;
    setActivatedTabs(prev => {
      if (prev.has(activeTab)) return prev;
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
    if (!loadedTabs.has(activeTab)) {
      setIsLoading(true);
    }
  }, [activeTab]);

  const handleIframeLoad = (key: string) => {
    setLoadedTabs(prev => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    if (key === activeTab) setIsLoading(false);
  };

  const activeTabInfo = allTabs.find(t => t.id === activeTab);
  const embedUrl = activeTabInfo?.url || "";
  const dashboardName = activeTabInfo?.name || "";

  if (loadingSite) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100vh - 64px)',
        marginTop: '64px',
        fontFamily: "'IBM Plex Sans', sans-serif",
        backgroundColor: isDarkMode ? '#0B1120' : '#FFFFFF'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: isDarkMode ? '3px solid #1E2B42' : '3px solid #E5E7EB',
          borderTopColor: '#0084CF',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }} />
        <div style={{ marginTop: '16px', color: isDarkMode ? '#94A3B8' : '#6B7280', fontWeight: 600 }}>
          Loading site configuration...
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!siteData) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100vh - 64px)',
        marginTop: '64px',
        fontFamily: "'IBM Plex Sans', sans-serif",
        backgroundColor: isDarkMode ? '#0B1120' : '#FFFFFF'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: '20px', marginBottom: '12px', color: '#EF4444', fontWeight: 600 }}>
            Site Not Found
          </h2>
          <p style={{ color: isDarkMode ? '#94A3B8' : '#6B7280', fontWeight: 400 }}>
            The requested site "{site?.toUpperCase()}" could not be loaded or does not exist.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      height: 'calc(100vh - 64px)',
      marginTop: '64px',
      display: 'flex',
      fontFamily: "'IBM Plex Sans', sans-serif",
      overflow: 'hidden'
    }}>
      {/* Main Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>

        {/* Sub-Tab Navigation Row */}
        {shouldShowSubTabs && (
          <div style={{
            display: 'flex',
            gap: '8px',
            backgroundColor: isDarkMode ? '#0F172A' : '#F9FAFB',
            padding: '10px 20px 12px',
            borderBottom: isDarkMode ? '1px solid #1E293B' : '1px solid #E5E7EB',
            overflowX: 'auto',
            alignItems: 'center'
          }}>
            {categoryTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: activeTab === tab.id ? '#FFFFFF' : (isDarkMode ? '#94A3B8' : '#6B7280'),
                  backgroundColor: activeTab === tab.id ? '#0084CF' : 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.backgroundColor = isDarkMode ? '#1E293B' : '#E5E7EB';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {tab.icon}
                <span>{tab.name}</span>
              </button>
            ))}
          </div>
        )}

        {/* Dashboard Container */}
        <div style={{
          flex: 1,
          position: 'relative',
          backgroundColor: isDarkMode ? '#0B1120' : '#FFFFFF',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}>

          {/* Live 2 Dashboard (direct native component matching iframe layout) */}
          <div style={{ display: activeCategory === 'live-2' ? 'block' : 'none', minHeight: '100%' }}>
            {activatedTabs.has('live-2-custom') && (
              <React.Suspense fallback={<DashboardFallback />}>
                <Live2Dashboard
                  key={site || 'default-site'}
                  site={site || ''}
                  siteName={siteData?.site_name}
                  siteLogoUrl={siteData?.logo_url || siteData?.company_logo || siteData?.site_logo}
                  companyId={siteData?.company_id}
                  companyName={siteData?.company_name}
                  siteData={siteData}
                />
              </React.Suspense>
            )}
          </div>

          {/* Performance Analytics 2 Dashboard */}
          <div style={{ display: activeCategory === 'performance-analytics-2' ? 'block' : 'none', height: '100%' }}>
            {activatedTabs.has('performance-analytics-2-custom') && (
              <React.Suspense fallback={<DashboardFallback />}>
                <PerformanceAnalytics2Dashboard
                  key={site || 'default-site'}
                  site={site || ''}
                  siteName={siteData?.site_name}
                  siteLogoUrl={siteData?.logo_url || siteData?.company_logo || siteData?.site_logo}
                  companyId={siteData?.company_id}
                  companyName={siteData?.company_name}
                />
              </React.Suspense>
            )}
          </div>

          {/* Historian Chart (direct, no Power BI) */}
          <div style={{ display: activeCategory === 'historian' ? 'block' : 'none', height: '100%' }}>
            {activatedTabs.has('historical-analysis') && (
              <React.Suspense fallback={<DashboardFallback />}>
                <HistorianChart site={site || ''} />
              </React.Suspense>
            )}
          </div>

          {/* Forced Outages Chart (direct, no Power BI) */}
          <div style={{ display: activeCategory === 'forced-outages' ? 'block' : 'none', height: '100%' }}>
            {activatedTabs.has('plant-breakdown-analysis') && (
              <React.Suspense fallback={<DashboardFallback />}>
                <ForcedOutagesChart site={site || ''} />
              </React.Suspense>
            )}
          </div>

          {/* Placeholder until VPP page is implemented */}
          <div style={{ display: activeCategory === 'virtual-power-plant' ? 'block' : 'none', height: '100%' }}>
            <div className="vpp-placeholder-wrap" style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDarkMode ? '#0B1120' : '#F8FAFC',
              padding: '24px'
            }}>
              <div className="vpp-placeholder-card" style={{
                maxWidth: '520px',
                width: '100%',
                background: isDarkMode ? '#131D31' : '#FFFFFF',
                border: isDarkMode ? '1px solid #1E2B42' : '1px solid #DFE6EE',
                borderRadius: '4px',
                padding: '40px',
                textAlign: 'center',
                boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.45)' : '0 1px 2px rgba(22,32,43,0.04)'
              }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '52px',
                  height: '52px',
                  borderRadius: '4px',
                  background: isDarkMode ? '#1E293B' : '#F2F8FC',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #C2DFF1',
                  color: '#0084CF',
                  marginBottom: '22px'
                }}>
                  <Zap size={24} strokeWidth={1.6} />
                </div>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '14px',
                  fontSize: '11px',
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: isDarkMode ? '#3EA3DA' : '#0066A3'
                }}>
                  <span style={{ width: '20px', height: '2px', background: '#0084CF' }} />
                  In development
                </div>
                <h2 style={{
                  fontSize: '24px',
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  marginBottom: '12px',
                  color: isDarkMode ? '#F1F5F9' : '#1C4D88',
                  fontWeight: 600
                }}>
                  Virtual Power Plant
                </h2>
                <p style={{
                  color: isDarkMode ? '#94A3B8' : '#52606F',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: 1.65,
                  margin: '0 auto',
                  maxWidth: '38ch'
                }}>
                  Aggregated dispatch and control across distributed generation and
                  storage assets. This module is not yet available for this site.
                </p>
              </div>
            </div>
          </div>

          {/* Fallback for no configured embed URL */}
          {(activeCategory === 'live' || activeCategory === 'performance-analytics') && !activeTab && (
            <div style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDarkMode ? '#0B1120' : '#FFFFFF',
              padding: '20px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '64px',
                  height: '64px',
                  backgroundColor: isDarkMode ? 'rgba(217, 119, 6, 0.2)' : '#FEF3C7',
                  borderRadius: '12px',
                  marginBottom: '16px',
                  color: '#D97706'
                }}>
                  <AlertTriangle size={32} />
                </div>
                <h2 style={{
                  fontSize: '20px',
                  marginBottom: '8px',
                  color: isDarkMode ? '#F1F5F9' : '#111827',
                  fontWeight: 800
                }}>
                  Dashboard Not Configured
                </h2>
                <p style={{ color: isDarkMode ? '#94A3B8' : '#6B7280', fontWeight: 700, maxWidth: '400px' }}>
                  No {activeCategory === 'live' ? 'Live' : 'Performance Analytics'} dashboard has been configured for {siteData?.site_name || site?.toUpperCase()}.
                </p>
              </div>
            </div>
          )}

          {/* Loading Overlay — only shown on first visit to a tab */}
          {activeCategory !== 'historian' && activeCategory !== 'forced-outages' && activeCategory !== 'live-2' && activeCategory !== 'performance-analytics-2' && activeCategory !== 'virtual-power-plant' && activeTab && isLoading && !loadedTabs.has(activeTab) && (
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDarkMode ? 'rgba(11, 17, 32, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              zIndex: 10
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '64px',
                  height: '64px',
                  backgroundColor: '#0084CF',
                  borderRadius: '12px',
                  marginBottom: '16px'
                }}>
                  {activeTabInfo?.icon || <Activity size={32} color="#FFFFFF" />}
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: isDarkMode ? '#F1F5F9' : '#111827',
                  marginBottom: '8px'
                }}>
                  Loading {dashboardName}
                </div>
                <div style={{
                  marginTop: '16px',
                  width: '40px',
                  height: '40px',
                  margin: '16px auto 0',
                  border: isDarkMode ? '3px solid #1E2B42' : '3px solid #E5E7EB',
                  borderTopColor: '#0084CF',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }} />
              </div>
            </div>
          )}

          {/* Power BI embeds — ALL tabs rendered once, shown/hidden via CSS.
               Uses SDK embed token (service-principal) — no user sign-in needed.
               Falls back to iframe+autoAuth if backend is not configured. */}
          {allTabs
            .filter(t => t.category !== 'historian' && t.category !== 'forced-outages' && t.category !== 'live-2' && t.category !== 'performance-analytics-2' && t.url)
            .map(tab => {
              const isVisible =
                activeCategory !== 'historian' &&
                activeCategory !== 'forced-outages' &&
                activeCategory !== 'live-2' &&
                activeCategory !== 'performance-analytics-2' &&
                tab.id === activeTab;
              const { reportId, appId, ctid } = parsePowerBIUrl(tab.url);
              return (
                <PowerBIEmbed
                  key={tab.id}
                  url={tab.url}
                  reportId={reportId}
                  appId={appId}
                  ctid={ctid}
                  title={`${site?.toUpperCase()} - ${tab.name}`}
                  isVisible={isVisible && activatedTabs.has(tab.id)}
                  onLoad={() => handleIframeLoad(tab.id)}
                />
              );
            })
          }
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default DashboardPage;
